import { c as _c } from "react/compiler-runtime";
// Tremor Raw Tooltip [v0.0.0]
import * as TooltipPrimitives from "@radix-ui/react-tooltip";
import React from "react";
import { cx } from "@/lib/tailwind-utils";
interface TooltipProps extends Omit<TooltipPrimitives.TooltipContentProps, "content" | "onClick">, Pick<TooltipPrimitives.TooltipProps, "open" | "defaultOpen" | "onOpenChange" | "delayDuration"> {
  content: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  side?: "bottom" | "left" | "top" | "right";
  showArrow?: boolean;
  triggerAsChild?: boolean;
  triggerOnlyHover?: boolean;
  variant?: "default" | "light";
}
const Tooltip = React.forwardRef<React.ElementRef<typeof TooltipPrimitives.Content>, TooltipProps>((t0, forwardedRef) => {
  const $ = _c(48);
  let children;
  let className;
  let content;
  let defaultOpen;
  let delayDuration;
  let onClick;
  let onOpenChange;
  let open;
  let props;
  let side;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== t0) {
    ({
      children,
      className,
      content,
      delayDuration,
      defaultOpen,
      open,
      onClick,
      onOpenChange,
      showArrow: t1,
      side,
      sideOffset: t2,
      triggerAsChild: t3,
      triggerOnlyHover: t4,
      variant: t5,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = content;
    $[4] = defaultOpen;
    $[5] = delayDuration;
    $[6] = onClick;
    $[7] = onOpenChange;
    $[8] = open;
    $[9] = props;
    $[10] = side;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
  } else {
    children = $[1];
    className = $[2];
    content = $[3];
    defaultOpen = $[4];
    delayDuration = $[5];
    onClick = $[6];
    onOpenChange = $[7];
    open = $[8];
    props = $[9];
    side = $[10];
    t1 = $[11];
    t2 = $[12];
    t3 = $[13];
    t4 = $[14];
    t5 = $[15];
  }
  const showArrow = t1 === undefined ? true : t1;
  const sideOffset = t2 === undefined ? 10 : t2;
  const triggerAsChild = t3 === undefined ? false : t3;
  const triggerOnlyHover = t4 === undefined ? false : t4;
  const variant = t5 === undefined ? "default" : t5;
  const isLight = variant === "light";
  if (!content) {
    let t6;
    if ($[16] !== children || $[17] !== onClick || $[18] !== triggerAsChild) {
      t6 = triggerAsChild ? children : <span onClick={onClick}>{children}</span>;
      $[16] = children;
      $[17] = onClick;
      $[18] = triggerAsChild;
      $[19] = t6;
    } else {
      t6 = $[19];
    }
    return t6;
  }
  let t6;
  if ($[20] !== children || $[21] !== onClick || $[22] !== triggerAsChild) {
    t6 = <TooltipPrimitives.Trigger onClick={onClick} asChild={triggerAsChild}>{children}</TooltipPrimitives.Trigger>;
    $[20] = children;
    $[21] = onClick;
    $[22] = triggerAsChild;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  const t7 = isLight ? "z-[9999] min-w-0 max-w-xs select-none overflow-hidden rounded-lg border border-border bg-background/60 px-3 py-2 shadow-lg backdrop-blur-lg" : "z-[1000] -mb-1.5 max-w-60 select-none overflow-hidden text-ellipsis rounded-md px-2.5 py-1.5 text-body leading-5 shadow-md";
  const t8 = isLight ? "text-foreground" : "text-gray-50";
  const t9 = !isLight && "bg-gray-900 dark:bg-gray-700";
  let t10;
  if ($[24] !== className || $[25] !== t7 || $[26] !== t8 || $[27] !== t9) {
    t10 = cx(t7, t8, t9, "will-change-[transform,opacity]", "data-[side=bottom]:animate-slideDownAndFade data-[side=left]:animate-slideLeftAndFade data-[side=right]:animate-slideRightAndFade data-[side=top]:animate-slideUpAndFade data-[state=closed]:animate-hide", className);
    $[24] = className;
    $[25] = t7;
    $[26] = t8;
    $[27] = t9;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== isLight || $[30] !== showArrow) {
    t11 = showArrow ? <TooltipPrimitives.Arrow className={cx("z-[1000] border-none", isLight ? "fill-background" : "fill-gray-900 dark:fill-gray-700")} width={12} height={7} aria-hidden="true" /> : null;
    $[29] = isLight;
    $[30] = showArrow;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== content || $[33] !== forwardedRef || $[34] !== props || $[35] !== side || $[36] !== sideOffset || $[37] !== t10 || $[38] !== t11) {
    t12 = <TooltipPrimitives.Portal><TooltipPrimitives.Content ref={forwardedRef} side={side} sideOffset={sideOffset} align="center" className={t10} {...props}>{content}{t11}</TooltipPrimitives.Content></TooltipPrimitives.Portal>;
    $[32] = content;
    $[33] = forwardedRef;
    $[34] = props;
    $[35] = side;
    $[36] = sideOffset;
    $[37] = t10;
    $[38] = t11;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  let t13;
  if ($[40] !== defaultOpen || $[41] !== delayDuration || $[42] !== onOpenChange || $[43] !== open || $[44] !== t12 || $[45] !== t6 || $[46] !== triggerOnlyHover) {
    t13 = <TooltipPrimitives.Provider delayDuration={150}><TooltipPrimitives.Root open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange} delayDuration={delayDuration} disableHoverableContent={triggerOnlyHover}>{t6}{t12}</TooltipPrimitives.Root></TooltipPrimitives.Provider>;
    $[40] = defaultOpen;
    $[41] = delayDuration;
    $[42] = onOpenChange;
    $[43] = open;
    $[44] = t12;
    $[45] = t6;
    $[46] = triggerOnlyHover;
    $[47] = t13;
  } else {
    t13 = $[47];
  }
  return t13;
});
Tooltip.displayName = "Tooltip";
export default Tooltip;
export { type TooltipProps };